// components/views/PAALView.jsx
import { useState, useEffect } from 'react';
import supabase from '../../utils/supabaseClient';
import { Field, Callout, UploadZone, LoadRow, Icons } from '../UI';
import { fmtMoney, fmtSize, fmtDate, today } from '../../utils/constants';

export default function PAALView({ paal, onSave, apiKey, callAI, toast }) {
  const [p, setP] = useState({ ...paal });
  const [paalFile, setPaalFile] = useState(paal.file || null);

  // Re-sync when paal loads from Supabase
  useEffect(() => {
    if (paal && (paal.nonIT || paal.it || paal.notes || paal.uploaded)) {
      setP({ ...paal });
      // If uploaded, show file metadata from paal prop (actual PDF in Supabase Storage)
      if (paal.uploaded && paal.file && !paalFile) {
        setPaalFile({ name: paal.file.name, size: paal.file.size, date: paal.file.date });
      }
    }
  }, [paal]); // eslint-disable-line
  const [extracting, setExtracting] = useState(false);

  function upd(k, v) { setP(prev => ({ ...prev, [k]: v })); }

  async function handleFileUpload(file) {
    setPaalFile(file);

    // Auto-extract thresholds if API key is set
    if (!apiKey) {
      toast.info('File uploaded. Add your API key in Settings to auto-extract thresholds.');
      return;
    }

    setExtracting(true);
    toast.info('Reading PAAL document…');
    try {
      const sys = 'You are a California state contracting specialist. Extract purchasing authority thresholds from this PAAL (Purchasing Authority Approval Letter) or similar delegation document. Return ONLY valid JSON with no markdown:\n{"nonIT": 50000, "it": 200000, "tier": "1", "agency": "", "effectiveDate": "", "expirationDate": "", "notes": ""}\n\nnonIT = Non-IT services dollar threshold (number only, no $ or commas)\nit = IT services dollar threshold (number only)\ntier = PAAL tier level (1, 2, or 3)\nIf a threshold is not found, use 0.';
      const raw = await callAI(apiKey, {
        system: sys,
        model: 'claude-haiku-4-5-20251001',
        max_tokens: 300,
        messages: [{ role: 'user', content: [
          { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: file.base64 } },
          { type: 'text', text: 'Extract the purchasing authority thresholds from this document.' },
        ]}],
        tag: 'paal_extraction',
      });

      const cleaned = raw.trim().replace(/^```[\w]*\n?/, '').replace(/\n?```$/, '').trim();
      const extracted = JSON.parse(cleaned);

      const updates = {};
      if (extracted.nonIT > 0) updates.nonIT = extracted.nonIT;
      if (extracted.it > 0) updates.it = extracted.it;
      if (extracted.tier) updates.tier = extracted.tier;
      if (extracted.agency) updates.agency = extracted.agency;
      if (extracted.effectiveDate) updates.effectiveDate = extracted.effectiveDate;
      if (extracted.expirationDate) updates.expirationDate = extracted.expirationDate;
      if (extracted.notes) updates.notes = extracted.notes;

      setP(prev => ({ ...prev, ...updates }));
      toast.success('Thresholds extracted — review and save to confirm.');
    } catch (e) {
      toast.warn('Could not auto-extract thresholds. Review the fields below and enter manually.');
      console.error('PAAL extraction error:', e);
    } finally {
      setExtracting(false);
    }
  }

  async function handleSave() {
    if (paalFile?.base64) {
      // Upload PDF to Supabase Storage
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const bytes = atob(paalFile.base64);
          const arr = new Uint8Array(bytes.length);
          for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
          const blob = new Blob([arr], { type: 'application/pdf' });
          await supabase.storage.from('paal-files').upload(
            user.id + '/paal.pdf', blob, { upsert: true }
          );
        }
      } catch (e) { console.error('PAAL upload error:', e); }
    }
    onSave({ ...p, uploaded: !!(paalFile || p.uploaded), file: paalFile ? { name: paalFile.name, size: paalFile.size, date: today() } : p.file });
  }

  async function downloadPaal() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { toast.warn('Not logged in'); return; }
      const { data, error } = await supabase.storage
        .from('paal-files').download(user.id + '/paal.pdf');
      if (error || !data) { toast.warn('File not found.'); return; }
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url; a.download = p.file?.name || 'PAAL.pdf';
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (e) {
      toast.warn('Could not download file.');
    }
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">PAAL & Authority</h1>
          <p className="page-subtitle">Purchasing Authority Approval Letter — thresholds for DGS/OLS routing determination</p>
        </div>
        <button className="btn btn-gold" onClick={handleSave}>Save PAAL</button>
      </div>

      <Callout type={p.uploaded ? 'green' : 'orange'}>
        {p.uploaded
          ? 'PAAL configured. DGS routing flags will use these thresholds across all contracts.'
          : 'Upload your PAAL document and Penny will extract your thresholds automatically.'}
      </Callout>

      {/* Upload section */}
      <div className="card">
        <h3 className="card-title">PAAL Document</h3>
        <p style={{ fontSize: 13, color: 'var(--slate-500)', marginBottom: 16, lineHeight: 1.6 }}>
          Upload your PAAL PDF. Penny will read it and fill in your authority thresholds automatically.
          The document is stored securely in GoldenScope.
        </p>

        {paalFile || p.file ? (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px',
            background: 'var(--green-50)', border: '1.5px solid var(--green-100)',
            borderRadius: 'var(--r-lg)', marginBottom: 12,
          }}>
            <div style={{ fontSize: 24 }}>📄</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--green-700)' }}>
                {paalFile?.name || p.file?.name}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--green-600)', marginTop: 2 }}>
                {paalFile?.size ? fmtSize(paalFile.size) : p.file?.size ? fmtSize(p.file.size) : ''}
                {(p.file?.date) ? ' · Uploaded ' + fmtDate(p.file.date) : ''}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {(paalFile?.base64 || p.file) && (
                <button className="btn btn-ghost btn-sm" onClick={downloadPaal}>
                  <Icons.download /> Download
                </button>
              )}
              <label className="btn btn-ghost btn-sm" style={{ cursor: 'pointer' }}>
                <Icons.upload /> Replace
                <input type="file" accept=".pdf" style={{ display: 'none' }}
                  onChange={e => { if (e.target.files[0]) {
                    const f = e.target.files[0];
                    const reader = new FileReader();
                    reader.onload = ev => handleFileUpload({ name: f.name, base64: ev.target.result.split(',')[1], size: f.size });
                    reader.readAsDataURL(f);
                  }; e.target.value = ''; }}
                />
              </label>
            </div>
          </div>
        ) : (
          <UploadZone
            onFile={handleFileUpload}
            title="Click or drag your PAAL PDF here"
            subtitle="Penny will automatically extract your purchasing authority thresholds"
            style={{ marginBottom: 12 }}
          />
        )}

        {extracting && (
          <LoadRow msg="Reading PAAL and extracting thresholds — just a moment…" />
        )}
      </div>

      {/* Thresholds */}
      <div className="card">
        <h3 className="card-title">Purchasing Authority Thresholds</h3>
        <p style={{ fontSize: 12.5, color: 'var(--slate-500)', marginBottom: 16 }}>
          Contracts above these amounts require DGS/OLS routing per your delegation. These are automatically applied across all contracts.
        </p>
        <div className="form-grid-2" style={{ marginBottom: 20 }}>
          <Field
            label="Non-IT Services Threshold ($)"
            value={String(p.nonIT || '')}
            onChange={v => upd('nonIT', parseFloat(v) || 0)}
            type="number"
            hint={'Contracts above ' + fmtMoney(p.nonIT) + ' require DGS/OLS routing'}
          />
          <Field
            label="IT Services Threshold ($)"
            value={String(p.it || '')}
            onChange={v => upd('it', parseFloat(v) || 0)}
            type="number"
            hint={'IT contracts above ' + fmtMoney(p.it) + ' require DGS/OLS routing'}
          />
        </div>

        <div className="form-grid-3">
          <Field label="PAAL Tier" value={p.tier || ''} onChange={v => upd('tier', v)} placeholder="e.g., 1" />
          <Field label="Purchasing Authority Number" value={p.purchasingAuthNum || ''} onChange={v => upd('purchasingAuthNum', v)} monospace placeholder="e.g., PA-12345" hint="Appears on STD 213 header" />
          <Field label="Effective Date" value={p.effectiveDate || ''} onChange={v => upd('effectiveDate', v)} type="date" />
          <Field label="Expiration Date" value={p.expirationDate || ''} onChange={v => upd('expirationDate', v)} type="date" />
        </div>

        <Field
          label="Notes"
          value={p.notes || ''}
          onChange={v => upd('notes', v)}
          rows={2}
          placeholder="e.g., Tier 1 authority per PAAL dated 01/01/2025, approved for Energy Safety Admin Services"
          style={{ marginTop: 12 }}
        />

        <div className="btn-row">
          <button className="btn btn-gold" onClick={handleSave}>Save PAAL</button>
          <button className="btn btn-ghost" onClick={() => {
            setP({ nonIT: 50000, it: 200000, notes: '', uploaded: false });
            setPaalFile(null);
          }}>Reset to Defaults</button>
        </div>
      </div>

      {/* How it works */}
      <div className="card">
        <h3 className="card-title">How Thresholds Are Applied</h3>
        <div style={{ fontSize: 13, lineHeight: 1.8, color: 'var(--slate-600)' }}>
          <p style={{ marginBottom: 10 }}>
            Every contract budget is automatically compared against these thresholds. Contracts that exceed your
            delegated authority are flagged with <strong>DGS/OTA</strong> and/or <strong>DGS/OLS</strong> badges throughout the app.
          </p>
          <p>DGS routing flags appear in:</p>
          <ul style={{ paddingLeft: 20, marginTop: 6 }}>
            <li>Dashboard contract list</li>
            <li>Contract header</li>
            <li>Stage &amp; Info tab (callout banner)</li>
            <li>Stage advance modal (warning before routing stage)</li>
            <li>Weekly Status Report</li>
            <li>Ask Penny recommendations</li>
          </ul>
          <p style={{ marginTop: 10, color: 'var(--slate-500)', fontSize: 12 }}>
            Per Public Contract Code §10295, contracts exceeding your PAAL threshold require DGS/OLS approval before execution.
          </p>
        </div>
      </div>
    </div>
  );
}
